import React from 'react'
import * as Yup from 'yup'
import { Grid } from '@mui/material'
import { BasicFormLayout, Form } from '@carsys/form'
import { CountryCode } from '@carsys/enums/country-code'
import { IWholesalerSupport, TicketSystem } from '@cbgms/api'
import GeneralFields from './GeneralFields'
import ServiceFields from './ServiceFields'
import { useTranslation } from 'react-i18next'
import { isValidPhone } from '@cbgms/base/utils/validations'
import Divider from '@mui/material/Divider'
import StockSettings from 'app-support/wholesale/components/StockSettings'
import OrderAcknowledgementSettings from 'app-support/wholesale/components/OrderAcknowledgementSettings'
import ImageUploader from 'app-support/ImageUploader/ImageUploader'
import { MediaCategory } from 'app-support/ImageUploader/media-category'
import JSONForm from './JSONForm'
import { useFeature } from '@cbgms/base/feature-flags'

export interface FormValues extends Omit<IWholesalerSupport, 'UpdatedAt' | 'CreatedAt'> {
  ServiceToggles: { [k: string]: boolean }
  ServiceConfig: { [k: string]: string }
  JSONFormFile?: File | null
  JSONFormName?: string
  JSONFormUploadSuccess?: boolean
}

interface Props {
  initialValues: FormValues
  onSubmit: (formValues: FormValues) => Promise<FormValues>
  onSubmitSuccess: (formValues: FormValues) => void
}

export const WholesalerForm: React.FC<Props> = ({ initialValues, onSubmit, onSubmitSuccess }) => {
  const { t } = useTranslation()
  const hasJsonUploadFeature = useFeature('carsys-support-wholesaler-json-upload')

  const validationSchema = Yup.object()
    .when({
      is: (input: FormValues) => !!input.CMSSystem,
      then: schema =>
        schema.shape({
          CMSURL: Yup.string().required().url(),
          CMSDrupalOverride: Yup.string().optional()
        })
    })
    .when({
      is: (input: FormValues) => input.TicketSystem === TicketSystem.Zendesk,
      then: schema =>
        schema.shape({
          TicketAgentEmail: Yup.string().required().email(),
          TicketURL: Yup.string().required(), // ticket url not actually an url, but just the subdomain part
          TicketAPIKey: Yup.string().required(),
          TicketBrandID: Yup.string().required()
        })
    })
    .when({
      is: (input: FormValues) => input.TicketSystem === TicketSystem.Email,
      then: schema =>
        schema.shape({
          TicketAgentEmail: Yup.string().required().email()
        })
    })
    .shape({
      Name: Yup.string().required(),
      OrderSystem: Yup.string().required(),
      LogoURL: Yup.string().required(),
      Country: Yup.mixed<CountryCode>().oneOf(Object.values(CountryCode)).required(),
      TermsAndConditionsURL: Yup.string().optional().url(),
      IFrameURL: Yup.string().optional().url(),
      IsActive: Yup.boolean().optional(),
      SupportEmail: Yup.string().optional().email(),
      SupportPhoneNumber: Yup.string()
        .optional()
        .test('SupportPhoneNumber', t('global.not_a_valid_phone'), input => !isValidPhone(input))
    })

  return (
    <Form
      // NavigationConfirmation should stay off until LinkCatalogToWholesaler is refactored.
      // Having multiple navigationPrompt(s) on the same page is causing issues.
      NavigationConfirmation={null}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onSubmitSuccess={onSubmitSuccess}
      validationSchema={validationSchema}
    >
      <BasicFormLayout backButtonProps={{ to: () => '/wholesale' }}>
        <Grid container spacing={2} sx={{ '& [role="separator"]': { marginLeft: 2, marginRight: 2 }, m: 0 }}>
          <Grid item xs>
            <GeneralFields />
          </Grid>
          <Divider orientation='vertical' flexItem role='separator' />
          <Grid item xs>
            <ImageUploader name='LogoURL' category={MediaCategory.Wholesaler} />
            <Divider variant='middle' sx={{ marginY: 1 }} />
            <ServiceFields />
            <OrderAcknowledgementSettings />
            <StockSettings />
            {initialValues.UUID && hasJsonUploadFeature && (
              <>
                <Divider variant='middle' sx={{ marginY: 1 }} />
                <JSONForm />
              </>
            )}
          </Grid>
        </Grid>
      </BasicFormLayout>
    </Form>
  )
}

export default WholesalerForm
